<script setup lang="ts">
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import Modal from '@components/Modal.vue';
import { useModal } from '@composables/useModal';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import GoogleMap, { type CircleOptions } from '@components/GoogleMap.vue';
import { trans } from '@tools/translation';
import { IconArrowsOutFillBlack } from '@icons';

interface Props {
  address: {
    latitude: number,
    longitude: number
  };
  circleOptions: CircleOptions
  zoom?: number;
}

withDefaults(defineProps<Props>(), {
  zoom: 14,
});

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const {
  isOpen: isGoogleMapModalOpen,
  openModal: openGoogleMapModal,
  closeModal: closeGoogleMapModal,
} = useModal();
</script>

<template>
  <div class="map-location relative">
    <GoogleMap
      :latitude="address.latitude"
      :longitude="address.longitude"
      :height="isMobileOrTablet ? 280 : 344"
      :zoom="14"
      is-static
      with-circle
      :circle-options="circleOptions"
      @click="openGoogleMapModal"
    />

    <Button
      variant="transparent"
      :class="[
        '!absolute bottom-1 right-1',
        '!w-fit',
        '!flex gap-2'
      ]"
      @click="openGoogleMapModal"
    >
      {{ trans('map.location.button.extend') }}

      <Icon
        :svg="IconArrowsOutFillBlack"
        width="24"
        height="24"
      />
    </Button>

    <Modal
      :is-opened="isGoogleMapModalOpen"
      :on-close="closeGoogleMapModal"
    >
      <template #header>
        {{ trans('global.map') }}
      </template>

      <template #body>
        <GoogleMap
          v-if="isGoogleMapModalOpen"
          :latitude="address.latitude"
          :longitude="address.longitude"
          height="100%"
          :zoom="zoom"
          :is-static="false"
          :has-fullscreen-control="false"
          has-transit-layer-control
          show-transit
          with-circle
          :circle-options="circleOptions"
          @click="openGoogleMapModal"
        />
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.map-location {
  :deep(.modal-container) {
    @apply w-full h-full;
  }
}
</style>
